import type { Composer } from 'vue-i18n';
import type { EngineError, EnginePanicError } from '#layers/base/types/error';
import type { ValueOf } from '#layers/base/types/utils';
import { translateString } from '#layers/i18n/public';
import { maybeResponseError } from './maybe-response-error';
import { isRecord } from './object';

export const ErrorCodes = {
	WORKSPACE_ACCESS_DENIED: 'WORKSPACE_ACCESS_DENIED',
} as const;

export type ErrorCode = ValueOf<typeof ErrorCodes>;

export function hasErrorCode(error: unknown, code: ErrorCode): boolean {
	if (!isRecord(error) || !('data' in error)) return false;

	const { data } = error;

	return isRecord(data) && 'code' in data && data.code === code;
}

/**
 * Formats an error message for display in the UI
 * Translates only strings that come from the Studio, Engine errors should be displayed as is.
 * TODO error codes
 */
export function formatError(error: unknown, i18n: Composer): string {
	if (typeof error === 'string') {
		return translateString(error, i18n);
	}

	if (isRecord(error)) {
		if (error.data) {
			if (isRecord(error.data) && typeof error.data.message === 'string' && error.data.message) {
				return error.data.message;
			}
			else if (typeof error.data === 'string') {
				return error.data;
			}
		}

		if (typeof error.message === 'string' && error.message) {
			return translateString(error.message, i18n);
		}
	}

	return i18n.t('errors.unknown_error');
}

export function toStructuredError(error: unknown): EngineError | undefined {
	if (typeof error === 'string') {
		return {
			message: error,
		};
	}
	else if (isRecord(error)) {
		const errorData = maybeResponseError(error);

		if (typeof errorData === 'string') {
			return {
				message: errorData,
			};
		}
		else if (isRecord(errorData)) {
			// The payload is trusted rather than validated: `message` is whatever the engine sent,
			// and the spread can override it with any shape.
			return {
				message: errorData.message,
				...errorData,
			} as EngineError;
		}
	}
}

export function getDeepestError(error: unknown): EngineError | undefined {
	if (isRecord(error) && error.source) {
		return getDeepestError(error.source);
	}

	// Wider than the declared return type — a value without a `source` comes back untouched,
	// including strings, numbers and `null`.
	return error as EngineError | undefined;
}

export function isEngineError(error: unknown): error is EngineError {
	return isRecord(error) && 'message' in error && ('source' in error || 'meta' in error);
}

export function isEnginePanicError(error: unknown): error is EnginePanicError {
	return isEngineError(error) && 'meta' in error && !!error.meta && 'isPanic' in error.meta && !!error.meta.isPanic;
}
